import type { ManualTransactionInput } from "@moneymap/contracts";
import {
  Button,
  Callout,
  Card,
  FormField,
  Modal,
  PageHeader,
  SectionHeader,
  SelectInput,
  StatePanel,
  StatusBadge,
  TextInput,
} from "@moneymap/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useApi } from "../api/ApiProvider";
import type { AccountRead, ActivityItem, CsvPreviewRead } from "../api/types";
import { QueryState } from "../components/QueryState";
import { type PreparedCsv, prepareCsvFile } from "../lib/csv";
import {
  formatHouseholdDate,
  formatInstant,
  formatMoney,
  parseMoneyInput,
  titleFromMachineValue,
} from "../lib/format";

type TransactionForm = {
  accountId: string;
  direction: "outflow" | "inflow";
  amount: string;
  householdDate: string;
  status: "posted" | "pending";
  kind: ManualTransactionInput["kind"];
  description: string;
  merchant: string;
  purposeId: string;
  canonicalCategory: string;
  note: string;
};

function toManualInput(values: TransactionForm, currency: string): ManualTransactionInput | null {
  const parsed = parseMoneyInput(values.amount);
  if (!parsed) return null;
  const absolute = BigInt(parsed) < 0n ? -BigInt(parsed) : BigInt(parsed);
  if (absolute === 0n) return null;
  const amountMinor = (values.direction === "outflow" ? -absolute : absolute).toString();
  return {
    accountId: values.accountId,
    amountMinor,
    currency,
    householdDate: values.householdDate,
    status: values.status,
    kind: values.kind,
    description: values.description,
    ...(values.merchant ? { merchant: values.merchant } : {}),
    ...(values.canonicalCategory ? { canonicalCategory: values.canonicalCategory } : {}),
    ...(values.purposeId ? { purposeId: values.purposeId } : {}),
    ...(values.note ? { note: values.note } : {}),
  };
}

function ManualTransactionModal({
  isOpen,
  onClose,
  accounts,
  currency,
}: {
  isOpen: boolean;
  onClose: () => void;
  accounts: AccountRead[];
  currency: string;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string>();
  const { register, handleSubmit, reset, formState } = useForm<TransactionForm>({
    defaultValues: {
      accountId: "",
      direction: "outflow",
      amount: "",
      householdDate: new Date().toISOString().slice(0, 10),
      status: "posted",
      kind: "purchase",
      description: "",
      merchant: "",
      purposeId: "",
      canonicalCategory: "",
      note: "",
    },
  });
  const mutation = useMutation({
    mutationFn: (input: ManualTransactionInput) => api.createManualTransaction(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["activity"] });
      await queryClient.invalidateQueries({ queryKey: ["briefing"] });
      reset();
      onClose();
    },
  });
  const submit = handleSubmit((values) => {
    setFormError(undefined);
    const input = toManualInput(values, currency);
    if (!input) {
      setFormError("Enter a non-zero amount in dollars and cents.");
      return;
    }
    mutation.mutate(input);
  });

  return (
    <Modal
      isOpen={isOpen}
      title="Add a manual transaction"
      description="Use the household perspective: money spent is an outflow; money received is an inflow."
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="manual-transaction-form" isLoading={mutation.isPending}>
            Save transaction
          </Button>
        </>
      }
    >
      <form id="manual-transaction-form" className="form-stack" onSubmit={submit}>
        <div className="form-grid form-grid--two">
          <FormField
            label="Account"
            htmlFor="transaction-account"
            error={formState.errors.accountId?.message}
          >
            <SelectInput
              id="transaction-account"
              {...register("accountId", { required: "Choose an account." })}
            >
              <option value="">Choose an account</option>
              {accounts.map((account) => (
                <option value={account.id} key={account.id}>
                  {account.name}
                </option>
              ))}
            </SelectInput>
          </FormField>
          <FormField label="Direction" htmlFor="transaction-direction">
            <SelectInput id="transaction-direction" {...register("direction")}>
              <option value="outflow">Money spent</option>
              <option value="inflow">Money received</option>
            </SelectInput>
          </FormField>
          <FormField label={"Amount (" + currency + ")"} htmlFor="transaction-amount">
            <TextInput
              id="transaction-amount"
              inputMode="decimal"
              placeholder="0.00"
              {...register("amount", { required: true })}
            />
          </FormField>
          <FormField label="Household date" htmlFor="transaction-date">
            <TextInput
              id="transaction-date"
              type="date"
              {...register("householdDate", { required: true })}
            />
          </FormField>
          <FormField label="Status" htmlFor="transaction-status">
            <SelectInput id="transaction-status" {...register("status")}>
              <option value="posted">Posted</option>
              <option value="pending">Pending</option>
            </SelectInput>
          </FormField>
          <FormField label="Type" htmlFor="transaction-kind">
            <SelectInput id="transaction-kind" {...register("kind")}>
              <option value="purchase">Purchase</option>
              <option value="inflow">Income</option>
              <option value="transfer">Transfer</option>
              <option value="refund">Refund</option>
              <option value="fee">Fee</option>
              <option value="interest">Interest</option>
              <option value="cash">Cash</option>
              <option value="payment">Payment</option>
              <option value="unknown">Not sure</option>
            </SelectInput>
          </FormField>
          <FormField
            label="Description"
            htmlFor="transaction-description"
            error={formState.errors.description?.message}
          >
            <TextInput
              id="transaction-description"
              {...register("description", { required: "Describe the transaction." })}
            />
          </FormField>
          <FormField label="Merchant or payee" htmlFor="transaction-merchant">
            <TextInput id="transaction-merchant" {...register("merchant")} />
          </FormField>
          <FormField
            label="Category"
            htmlFor="transaction-category"
            hint="Optional. Purpose can be corrected after saving."
          >
            <TextInput id="transaction-category" {...register("canonicalCategory")} />
          </FormField>
        </div>
        <FormField label="Note" htmlFor="transaction-note">
          <textarea id="transaction-note" className="mm-input" {...register("note")} />
        </FormField>
        {formError ? (
          <Callout title="Check the amount" tone="warning" role="alert">
            {formError}
          </Callout>
        ) : null}
        {mutation.error ? (
          <Callout title="Transaction not saved" tone="urgent" role="alert">
            {mutation.error.message}
          </Callout>
        ) : null}
      </form>
    </Modal>
  );
}

export function ActivityPage() {
  const api = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isManualOpen, setIsManualOpen] = useState(false);
  const filters = useMemo(
    () => ({
      q: searchParams.get("q") ?? undefined,
      account: searchParams.get("account") ?? undefined,
      purpose: searchParams.get("purpose") ?? undefined,
      type: searchParams.get("type") ?? undefined,
      status: searchParams.get("status") ?? undefined,
      from: searchParams.get("from") ?? undefined,
      to: searchParams.get("to") ?? undefined,
      cursor: searchParams.get("cursor") ?? undefined,
    }),
    [searchParams],
  );
  const activityQuery = useQuery({
    queryKey: ["activity", filters],
    queryFn: () => api.getActivity(filters),
  });
  const accountsQuery = useQuery({ queryKey: ["accounts"], queryFn: () => api.getAccounts() });
  const purposesQuery = useQuery({ queryKey: ["purposes"], queryFn: () => api.getPurposes() });
  const items = activityQuery.data?.data.items ?? [];
  const accounts = accountsQuery.data?.data ?? [];
  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("cursor");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Understand and correct"
        title="Activity"
        description="Search transactions, payments, deposits, transfers, Spark entries, and manual adjustments."
        actions={
          <div className="button-row">
            <Button type="button" onClick={() => setIsManualOpen(true)}>
              Add transaction
            </Button>
            <Link className="mm-button mm-button--secondary" to="/activity/import">
              Import CSV
            </Link>
          </div>
        }
      />
      <div className="filter-bar">
        <FormField label="Search" htmlFor="activity-search">
          <TextInput
            id="activity-search"
            type="search"
            value={searchParams.get("q") ?? ""}
            onChange={(event) => updateFilter("q", event.currentTarget.value)}
            placeholder="Description or merchant"
          />
        </FormField>
        <FormField label="Account" htmlFor="activity-account-filter">
          <SelectInput
            id="activity-account-filter"
            value={searchParams.get("account") ?? ""}
            onChange={(event) => updateFilter("account", event.currentTarget.value)}
          >
            <option value="">All accounts</option>
            {accounts.map((account) => (
              <option value={account.id} key={account.id}>
                {account.name}
              </option>
            ))}
          </SelectInput>
        </FormField>
        <FormField label="Purpose" htmlFor="activity-purpose-filter">
          <SelectInput
            id="activity-purpose-filter"
            value={searchParams.get("purpose") ?? ""}
            onChange={(event) => updateFilter("purpose", event.currentTarget.value)}
          >
            <option value="">All purposes</option>
            {(purposesQuery.data?.data ?? []).map((purpose) => (
              <option value={purpose.id} key={purpose.id}>
                {purpose.name}
              </option>
            ))}
          </SelectInput>
        </FormField>
        <FormField label="Type" htmlFor="activity-type-filter">
          <SelectInput
            id="activity-type-filter"
            value={searchParams.get("type") ?? ""}
            onChange={(event) => updateFilter("type", event.currentTarget.value)}
          >
            <option value="">All types</option>
            <option value="purchase">Purchases</option>
            <option value="inflow">Income</option>
            <option value="transfer">Transfers</option>
            <option value="payment">Payments</option>
            <option value="refund">Refunds</option>
            <option value="fee">Fees</option>
          </SelectInput>
        </FormField>
        <FormField label="Status" htmlFor="activity-status-filter">
          <SelectInput
            id="activity-status-filter"
            value={searchParams.get("status") ?? ""}
            onChange={(event) => updateFilter("status", event.currentTarget.value)}
          >
            <option value="">Posted and pending</option>
            <option value="posted">Posted</option>
            <option value="pending">Pending</option>
          </SelectInput>
        </FormField>
        <div className="form-grid form-grid--two">
          <FormField label="From" htmlFor="activity-from">
            <TextInput
              id="activity-from"
              type="date"
              value={searchParams.get("from") ?? ""}
              onChange={(event) => updateFilter("from", event.currentTarget.value)}
            />
          </FormField>
          <FormField label="To" htmlFor="activity-to">
            <TextInput
              id="activity-to"
              type="date"
              value={searchParams.get("to") ?? ""}
              onChange={(event) => updateFilter("to", event.currentTarget.value)}
            />
          </FormField>
        </div>
      </div>
      <QueryState
        isPending={activityQuery.isPending}
        isError={activityQuery.isError}
        error={activityQuery.error}
        onRetry={() => void activityQuery.refetch()}
        loadingTitle="Loading activity"
      >
        {items.length === 0 ? (
          <StatePanel
            title="No activity matches these filters"
            action={
              <Button type="button" variant="secondary" onClick={() => setSearchParams({})}>
                Clear filters
              </Button>
            }
          >
            Add a manual transaction or import a CSV when this household has activity to review.
          </StatePanel>
        ) : (
          <Card>
            <div className="card-list">
              {items.map((item) => (
                <Link className="list-row" key={item.id} to={"/activity/" + item.id}>
                  <div className="list-row__main">
                    <strong>{item.merchant || item.description}</strong>
                    <span>
                      {formatHouseholdDate(item.householdDate)} · {item.accountName} ·{" "}
                      {item.purposeName ?? "Purpose not confirmed"}
                    </span>
                  </div>
                  <div className="list-row__side">
                    <strong>{formatMoney(item.amountMinor, item.currency)}</strong>
                    {item.status === "pending" ? (
                      <StatusBadge status="pending" />
                    ) : (
                      <span className="small muted">{titleFromMachineValue(item.kind)}</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        )}
        {activityQuery.data?.data.nextCursor ? (
          <Button
            type="button"
            variant="secondary"
            onClick={() => updateFilter("cursor", activityQuery.data?.data.nextCursor ?? "")}
          >
            Load more
          </Button>
        ) : null}
      </QueryState>
      <ManualTransactionModal
        isOpen={isManualOpen}
        onClose={() => setIsManualOpen(false)}
        accounts={accounts}
        currency={accounts[0]?.currency ?? "USD"}
      />
    </div>
  );
}

export function ActivityDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [correction, setCorrection] = useState({
    kind: "",
    purposeId: "",
    canonicalCategory: "",
    ruleScope: "transaction",
  });
  const query = useQuery({ queryKey: ["activity", id], queryFn: () => api.getActivityItem(id) });
  const purposesQuery = useQuery({ queryKey: ["purposes"], queryFn: () => api.getPurposes() });
  const correctionMutation = useMutation({
    mutationFn: () =>
      api.correctActivity(id, {
        ...(correction.kind ? { kind: correction.kind } : {}),
        ...(correction.purposeId ? { purposeId: correction.purposeId } : {}),
        ...(correction.canonicalCategory
          ? { canonicalCategory: correction.canonicalCategory }
          : {}),
        ruleScope: correction.ruleScope,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["activity"] });
      await queryClient.invalidateQueries({ queryKey: ["briefing"] });
      await query.refetch();
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (item: ActivityItem) => api.deleteManualTransaction(item.id, item.version),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["activity"] });
      navigate("/activity", { replace: true });
    },
  });
  const item = query.data?.data;
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Activity detail"
        title={item?.merchant || item?.description || "Transaction"}
      />
      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
      >
        {item ? (
          <>
            <Card>
              <strong className="amount-primary">
                {formatMoney(item.amountMinor, item.currency)}
              </strong>
              <div className="summary-grid">
                <div className="summary-tile">
                  <span>Date</span>
                  <strong>{formatHouseholdDate(item.householdDate)}</strong>
                </div>
                <div className="summary-tile">
                  <span>Account</span>
                  <strong>{item.accountName}</strong>
                </div>
                <div className="summary-tile">
                  <span>Status</span>
                  <strong>{titleFromMachineValue(item.status)}</strong>
                </div>
                <div className="summary-tile">
                  <span>Source</span>
                  <strong>{item.source === "csv" ? "CSV import" : "Manual entry"}</strong>
                </div>
                <div className="summary-tile">
                  <span>Purpose</span>
                  <strong>{item.purposeName ?? "Needs confirmation"}</strong>
                </div>
                <div className="summary-tile">
                  <span>Confidence</span>
                  <strong>{titleFromMachineValue(item.confidence)}</strong>
                </div>
              </div>
              {item.note ? <p>{item.note}</p> : null}
            </Card>
            <Card>
              <SectionHeader
                title="Correct IterNest Money’s understanding"
                description="The raw source stays intact. A durable rule can apply the correction to future strong matches."
              />
              <form
                className="form-stack"
                onSubmit={(event) => {
                  event.preventDefault();
                  correctionMutation.mutate();
                }}
              >
                <div className="form-grid form-grid--two">
                  <FormField label="Transaction type" htmlFor="correction-kind">
                    <SelectInput
                      id="correction-kind"
                      value={correction.kind}
                      onChange={(event) =>
                        setCorrection({ ...correction, kind: event.currentTarget.value })
                      }
                    >
                      <option value="">Keep current</option>
                      <option value="purchase">Purchase</option>
                      <option value="inflow">Income</option>
                      <option value="transfer">Internal transfer</option>
                      <option value="refund">Refund</option>
                      <option value="fee">Fee</option>
                      <option value="payment">Bill or debt payment</option>
                      <option value="unknown">Unrelated / unknown</option>
                    </SelectInput>
                  </FormField>
                  <FormField label="Purpose" htmlFor="correction-purpose">
                    <SelectInput
                      id="correction-purpose"
                      value={correction.purposeId}
                      onChange={(event) =>
                        setCorrection({ ...correction, purposeId: event.currentTarget.value })
                      }
                    >
                      <option value="">Keep current</option>
                      {(purposesQuery.data?.data ?? []).map((purpose) => (
                        <option value={purpose.id} key={purpose.id}>
                          {purpose.name}
                        </option>
                      ))}
                    </SelectInput>
                  </FormField>
                  <FormField label="Category" htmlFor="correction-category">
                    <TextInput
                      id="correction-category"
                      value={correction.canonicalCategory}
                      onChange={(event) =>
                        setCorrection({
                          ...correction,
                          canonicalCategory: event.currentTarget.value,
                        })
                      }
                    />
                  </FormField>
                  <FormField label="Apply to" htmlFor="correction-scope">
                    <SelectInput
                      id="correction-scope"
                      value={correction.ruleScope}
                      onChange={(event) =>
                        setCorrection({ ...correction, ruleScope: event.currentTarget.value })
                      }
                    >
                      <option value="transaction">This transaction</option>
                      <option value="future">Future strong matches</option>
                      <option value="historical_and_future">
                        History and future strong matches
                      </option>
                    </SelectInput>
                  </FormField>
                </div>
                {correctionMutation.error ? (
                  <Callout title="Correction not saved" tone="urgent">
                    {correctionMutation.error.message}
                  </Callout>
                ) : null}
                {correctionMutation.isSuccess ? (
                  <Callout title="Plan updated" tone="positive" role="status">
                    IterNest Money saved the correction and requested a new calculation.
                  </Callout>
                ) : null}
                <div className="button-row">
                  <Button type="submit" isLoading={correctionMutation.isPending}>
                    Save correction
                  </Button>
                </div>
              </form>
            </Card>
            {item.source === "manual" ? (
              <Card className="danger-zone">
                <SectionHeader
                  title="Delete manual transaction"
                  description="Deleting removes it from active calculations but preserves the audit trail."
                />
                <Button type="button" variant="danger" onClick={() => setIsDeleteOpen(true)}>
                  Delete transaction
                </Button>
              </Card>
            ) : null}
          </>
        ) : null}
      </QueryState>
      <Modal
        isOpen={isDeleteOpen}
        title="Delete this transaction?"
        description="This changes coverage and recommendations. The source history remains auditable."
        onClose={() => setIsDeleteOpen(false)}
        footer={
          <>
            <Button type="button" variant="quiet" onClick={() => setIsDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={deleteConfirmation !== "DELETE"}
              isLoading={deleteMutation.isPending}
              onClick={() => {
                if (item) deleteMutation.mutate(item);
              }}
            >
              Delete transaction
            </Button>
          </>
        }
      >
        <FormField label="Type DELETE to confirm" htmlFor="delete-transaction-confirmation">
          <TextInput
            id="delete-transaction-confirmation"
            value={deleteConfirmation}
            onChange={(event) => setDeleteConfirmation(event.currentTarget.value)}
          />
        </FormField>
      </Modal>
    </div>
  );
}

type MappingKey =
  | "date"
  | "description"
  | "amount"
  | "debit"
  | "credit"
  | "status"
  | "category"
  | "balance";

const mappingLabels: Record<MappingKey, string> = {
  date: "Date",
  description: "Description",
  amount: "Signed amount",
  debit: "Debit",
  credit: "Credit",
  status: "Status",
  category: "Category",
  balance: "Balance",
};

export function CsvImportPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [accountId, setAccountId] = useState("");
  const [prepared, setPrepared] = useState<PreparedCsv>();
  const [preview, setPreview] = useState<CsvPreviewRead>();
  const [mapping, setMapping] = useState<Partial<Record<MappingKey, string | undefined>>>({});
  const [decisions, setDecisions] = useState<Record<number, "import" | "skip">>({});
  const [localError, setLocalError] = useState<string>();
  const accountsQuery = useQuery({ queryKey: ["accounts"], queryFn: () => api.getAccounts() });
  const previewMutation = useMutation({
    mutationFn: (input: {
      prepared: PreparedCsv;
      mapping?: Partial<Record<MappingKey, string | undefined>>;
    }) =>
      api.previewCsv({
        accountId,
        fileName: input.prepared.fileName,
        fileSize: input.prepared.fileSize,
        fileHash: input.prepared.fileHash,
        encoding: input.prepared.encoding,
        delimiter: input.prepared.delimiter,
        dateFormat: input.prepared.dateFormat,
        decimalSeparator: input.prepared.decimalSeparator,
        rows: input.prepared.rows,
        ...(input.mapping?.date && input.mapping.description
          ? {
              mapping: {
                date: input.mapping.date,
                description: input.mapping.description,
                ...(input.mapping.amount ? { amount: input.mapping.amount } : {}),
                ...(input.mapping.debit ? { debit: input.mapping.debit } : {}),
                ...(input.mapping.credit ? { credit: input.mapping.credit } : {}),
                ...(input.mapping.status ? { status: input.mapping.status } : {}),
                ...(input.mapping.category ? { category: input.mapping.category } : {}),
                ...(input.mapping.balance ? { balance: input.mapping.balance } : {}),
              },
            }
          : {}),
      }),
    onSuccess: (response) => {
      const data = response.data;
      setPreview(data);
      setMapping(data.suggestedMapping);
      const initial: Record<number, "import" | "skip"> = {};
      for (const row of data.rows) {
        initial[row.rowNumber] =
          row.status === "ready" || row.status === "warning" ? "import" : "skip";
      }
      setDecisions(initial);
    },
  });
  const confirmMutation = useMutation({
    mutationFn: (currentPreview: CsvPreviewRead) =>
      api.confirmCsv({
        importSessionId: currentPreview.importSessionId,
        fileHash: currentPreview.fileHash,
        expectedPreviewVersion: currentPreview.previewVersion,
        decisions: currentPreview.rows.map((row) => ({
          rowNumber: row.rowNumber,
          action: decisions[row.rowNumber] ?? "skip",
          ...(decisions[row.rowNumber] === "import" && row.normalized
            ? { normalizedTransaction: row.normalized }
            : {}),
        })),
      }),
    onSuccess: (response) => navigate("/activity/import/history?completed=" + response.data.id),
  });

  const selectFile = async (file: File | undefined) => {
    setLocalError(undefined);
    setPreview(undefined);
    if (!file) return;
    try {
      const result = await prepareCsvFile(file);
      setPrepared(result);
      if (!accountId) {
        setLocalError("Choose the account this export belongs to before previewing it.");
        return;
      }
      previewMutation.mutate({ prepared: result });
    } catch (error) {
      setLocalError(
        error instanceof Error ? error.message : "IterNest Money could not read this CSV safely.",
      );
    }
  };
  const rowsToImport = Object.values(decisions).filter((value) => value === "import").length;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Safe CSV import"
        title="Bring in bank or card activity"
        description="IterNest Money previews every row, keeps malformed data out, detects duplicates, and imports only after your confirmation."
        actions={
          <Link className="text-link" to="/activity/import/history">
            Import history
          </Link>
        }
      />
      {!preview ? (
        <Card>
          <form className="form-stack" onSubmit={(event) => event.preventDefault()}>
            <FormField
              label="Account"
              htmlFor="csv-account"
              hint="Each CSV imports into exactly one account."
            >
              <SelectInput
                id="csv-account"
                value={accountId}
                onChange={(event) => setAccountId(event.currentTarget.value)}
              >
                <option value="">Choose an account</option>
                {(accountsQuery.data?.data ?? []).map((account) => (
                  <option value={account.id} key={account.id}>
                    {account.name}
                  </option>
                ))}
              </SelectInput>
            </FormField>
            <FormField
              label="CSV file"
              htmlFor="csv-file"
              hint="UTF-8 or UTF-16, up to 20 MB and 50,000 rows. The file stays in this preview request; authenticated APIs are never service-worker cached."
            >
              <TextInput
                id="csv-file"
                type="file"
                accept=".csv,text/csv"
                disabled={!accountId || previewMutation.isPending}
                onChange={(event) => void selectFile(event.currentTarget.files?.[0])}
              />
            </FormField>
            {previewMutation.isPending ? (
              <Callout title="Reading the export" tone="info" role="status">
                Detecting columns and checking each row. Nothing has been imported.
              </Callout>
            ) : null}
            {localError ? (
              <Callout title="This file needs attention" tone="warning" role="alert">
                {localError}
              </Callout>
            ) : null}
            {previewMutation.error ? (
              <Callout title="Preview could not be created" tone="urgent" role="alert">
                {previewMutation.error.message}
              </Callout>
            ) : null}
          </form>
        </Card>
      ) : (
        <>
          <Callout title="Nothing has been imported yet" tone="info">
            Review column mapping, warnings, and duplicates. Confirmation is one transactional
            import.
          </Callout>
          {prepared?.localWarnings.length ? (
            <Callout title="Local structure warnings" tone="warning">
              <ul>
                {prepared.localWarnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </Callout>
          ) : null}
          <Card>
            <SectionHeader
              title="Column mapping"
              description="IterNest Money detected these columns. Correct them before the final preview."
            />
            <div className="form-grid form-grid--two">
              {(Object.keys(mappingLabels) as MappingKey[]).map((key) => (
                <FormField label={mappingLabels[key]} htmlFor={"mapping-" + key} key={key}>
                  <SelectInput
                    id={"mapping-" + key}
                    value={mapping[key] ?? ""}
                    onChange={(event) =>
                      setMapping({ ...mapping, [key]: event.currentTarget.value || undefined })
                    }
                  >
                    <option value="">Not mapped</option>
                    {preview.headers.map((header) => (
                      <option value={header} key={header}>
                        {header}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>
              ))}
            </div>
            <div className="button-row">
              <Button
                type="button"
                variant="secondary"
                disabled={
                  !prepared ||
                  !mapping.date ||
                  !mapping.description ||
                  (!mapping.amount && !mapping.debit && !mapping.credit)
                }
                isLoading={previewMutation.isPending}
                onClick={() => {
                  if (prepared) previewMutation.mutate({ prepared, mapping });
                }}
              >
                Recheck with this mapping
              </Button>
            </div>
          </Card>
          <Card>
            <SectionHeader
              title="Import preview"
              description={
                String(preview.totals.rows) +
                " rows · " +
                String(preview.totals.duplicates) +
                " duplicates · " +
                String(preview.totals.invalid) +
                " invalid"
              }
            />
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Use</th>
                    <th>Row</th>
                    <th>Transaction</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row) => (
                    <tr key={row.rowNumber}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={"Import row " + String(row.rowNumber)}
                          checked={decisions[row.rowNumber] === "import"}
                          disabled={row.status === "invalid"}
                          onChange={(event) =>
                            setDecisions({
                              ...decisions,
                              [row.rowNumber]: event.currentTarget.checked ? "import" : "skip",
                            })
                          }
                        />
                      </td>
                      <td>{row.rowNumber}</td>
                      <td>
                        {row.normalized ? (
                          <>
                            <strong>{row.normalized.description}</strong>
                            <div className="small muted">
                              {formatHouseholdDate(row.normalized.householdDate)} ·{" "}
                              {formatMoney(row.normalized.amountMinor, row.normalized.currency)}
                            </div>
                          </>
                        ) : (
                          <span className="muted">Could not normalize</span>
                        )}
                        {row.warnings.length > 0 ? (
                          <ul className="small">
                            {row.warnings.map((warning) => (
                              <li key={warning}>{warning}</li>
                            ))}
                          </ul>
                        ) : null}
                      </td>
                      <td>
                        <StatusBadge
                          status={
                            row.status === "duplicate"
                              ? "needs_confirmation"
                              : row.status === "invalid"
                                ? "at_risk"
                                : row.status === "warning"
                                  ? "partially_covered"
                                  : "covered"
                          }
                        />
                        {row.duplicateOfTransactionId ? (
                          <div className="small muted">Matches saved activity</div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          {confirmMutation.error ? (
            <Callout title="Import did not complete" tone="urgent" role="alert">
              {confirmMutation.error.message} No partial import was accepted.
            </Callout>
          ) : null}
          <div className="button-row button-row--end">
            <Button
              type="button"
              variant="quiet"
              onClick={() => {
                setPreview(undefined);
                setPrepared(undefined);
              }}
            >
              Choose another file
            </Button>
            <Button
              type="button"
              isLoading={confirmMutation.isPending}
              disabled={rowsToImport === 0}
              onClick={() => confirmMutation.mutate(preview)}
            >
              Confirm {rowsToImport} {rowsToImport === 1 ? "transaction" : "transactions"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function CsvImportHistoryPage() {
  const api = useApi();
  const [searchParams] = useSearchParams();
  const completedId = searchParams.get("completed");
  const query = useQuery({
    queryKey: ["csv-import-history"],
    queryFn: () => api.getImportHistory(),
    refetchInterval: 5_000,
  });
  const imports = query.data?.data ?? [];
  const completed = completedId ? imports.find((item) => item.id === completedId) : undefined;
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Audit and safe re-import"
        title="CSV import history"
        description="File fingerprints and row identities make a repeated import safe to preview. IterNest Money never silently duplicates the same source rows."
        actions={
          <Link className="mm-button mm-button--secondary" to="/activity/import">
            Import another CSV
          </Link>
        }
      />
      {completed ? (
        <Callout title="Import complete" tone="positive" role="status">
          {completed.importedCount} imported, {completed.duplicateCount} duplicates skipped,{" "}
          {completed.skippedCount} other rows skipped. Recalculation was requested.
        </Callout>
      ) : null}
      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
      >
        {imports.length === 0 ? (
          <StatePanel title="No CSV imports yet">
            Import history appears after the first preview is created.
          </StatePanel>
        ) : (
          <div className="card-list">
            {imports.map((item) => (
              <Card key={item.id}>
                <div className="list-row">
                  <div className="list-row__main">
                    <strong>{item.fileName}</strong>
                    <span>
                      {item.accountName} · {formatInstant(item.createdAt, "UTC")}
                    </span>
                  </div>
                  <div className="list-row__side">
                    <StatusBadge
                      status={
                        item.status === "completed"
                          ? "plan_updated"
                          : item.status === "failed"
                            ? "at_risk"
                            : "updating"
                      }
                    />
                    <span className="small muted">
                      {item.importedCount} imported · {item.duplicateCount} duplicates
                    </span>
                  </div>
                </div>
                {item.failureMessage ? (
                  <Callout title="Import failed safely" tone="urgent">
                    {item.failureMessage}
                  </Callout>
                ) : null}
              </Card>
            ))}
          </div>
        )}
      </QueryState>
    </div>
  );
}
